import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, ArrowUpRight } from "lucide-react";
import { NAV, CONTACT } from "../lib/site";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const lightPage = location.pathname === "/admin";
  const solid = scrolled || lightPage;

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          solid ? "bg-white/90 backdrop-blur-md shadow-[0_1px_0_0_#E2E8F0]" : "bg-transparent"
        }`}
      >
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-12 h-[84px] flex items-center">
          <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3 shrink-0">
            <img src="/logo192.png" alt="Saarthva logo" className="w-10 h-10 rounded-xl ring-1 ring-white/20" />
            <span className="leading-none">
              <span className={`block font-display font-extrabold tracking-tight text-lg ${solid || open ? "text-navy" : "text-white"}`}>
                SAARTHVA
              </span>
              <span className="block text-[9px] font-semibold tracking-[0.32em] text-saffron">VENTURES</span>
            </span>
          </Link>

          <nav className="hidden xl:flex items-center gap-6 mx-auto" data-testid="desktop-nav">
            {NAV.slice(1).map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`whitespace-nowrap text-sm font-semibold tracking-wide transition-colors ${
                  location.pathname === item.path
                    ? "text-saffron"
                    : solid
                      ? "text-slate-600 hover:text-navy"
                      : "text-slate-200 hover:text-white"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3 ml-auto xl:ml-0">
            <a
              href={`tel:+${CONTACT.phoneIntl}`}
              data-testid="header-phone-link"
              className={`hidden xl:flex items-center gap-2 whitespace-nowrap text-sm font-semibold rounded-full border px-5 py-2.5 transition-colors ${
                solid ? "border-line text-navy hover:border-steel" : "border-white/25 text-white hover:border-white/60"
              }`}
            >
              <Phone className="w-3.5 h-3.5 text-saffron" />
              {CONTACT.phoneDisplay}
            </a>
            <Link
              to="/request-quote"
              data-testid="header-request-quote-button"
              className="hidden sm:inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-saffron hover:bg-saffrondark text-white text-sm font-semibold px-6 py-3 transition-colors"
            >
              Request a Quote
              <ArrowUpRight className="w-4 h-4" />
            </Link>
            <button
              data-testid="mobile-menu-button"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
              className={`xl:hidden p-2 rounded-lg ${solid || open ? "text-navy" : "text-white"}`}
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-navy text-white flex flex-col justify-center px-8 xl:hidden"
          >
            <nav className="space-y-1">
              {[...NAV, { label: "Request a Quote", path: "/request-quote" }].map((item, i) => (
                <motion.button
                  key={item.path}
                  data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.4 }}
                  onClick={() => navigate(item.path)}
                  className={`block w-full text-left font-display text-3xl font-bold py-2 ${
                    location.pathname === item.path ? "text-saffron" : "text-white"
                  }`}
                >
                  {item.label}
                </motion.button>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-10 pt-6 border-t border-white/10 text-sm text-slate-400 space-y-1"
            >
              <p>{CONTACT.phoneDisplay}</p>
              <p>{CONTACT.email}</p>
              <p className="text-saffron font-mono text-xs tracking-widest pt-2">SUPPLY. SOURCE. RUN.</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
